import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFormValidation from '../../../../../hooks/useFormValidation';
import useNewStudyFieldsConfig from './useNewStudyFieldsConfig';
import { NewSubstanceStudyBody, useCreateCarcinogenicSubstanceStudyMutation, useDeleteCarcinogenicSubstanceStudyMutation } from '../../../../../services/carcinogenicApi';
import useAlerts from './useAlerts';
import { handleErrors } from '../../../../../utils/common';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch } from '../../../../../hooks/reduxHooks';
import { showAlert } from '../../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../../constants/values';

export interface NewStudyFormData {
    type: string | null;
    detail: string | null;
    frequency: string | null;
    frequencyQuantity: string | null;
}

const INITIAL_VALUES = {
    type: '',
    detail: '',
    frequency: '',
    frequencyQuantity: '',
};

const useNewStudyForm = ({
    selectedSubstanceId,
    selectedCarcinogenicReportId
}: {
    selectedSubstanceId: number | null;
    selectedCarcinogenicReportId: number | null;
}) => {
    const dispatch = useAppDispatch();
    const [formData, setFormData] = useState(INITIAL_VALUES);
    const [noComercialNameChecked, setNoComercialNameChecked] = useState<boolean>(false);

    const [createNewSubstanceStudy, { isLoading: isCreatingNewSubstanceStudy }] =
        useCreateCarcinogenicSubstanceStudyMutation();

    const [deleteSubstanceStudy, { isLoading: isDeletingSubstanceStudy }] =
        useDeleteCarcinogenicSubstanceStudyMutation();

    const { t } = useTranslation('carcinogenicReport', {
        keyPrefix: 'modules.substancesAndPreventiveMeasures'
    });

    const { showDeleteStudyAlert } = useAlerts({ translate: t });

    const {
        errors,
        hasValidationErrors,
        validateForm,
        handleBlurValidation,
        resetValidation,
        setErrors
    } = useFormValidation();

    const { newStudyFieldsConfig } = useNewStudyFieldsConfig({
        t,
        newStudyFormData: formData,
        handleBlurValidation
    });

    const handleChange = (name: string, value: any) => {
        setErrors((prevState) => ({
            ...prevState,
            [name]: { hasError: false, errorMessage: '' }
        }));
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSubmitStudy = async () => {
        if (!selectedSubstanceId || !selectedCarcinogenicReportId) return;
        if (!validateForm(formData, newStudyFieldsConfig)) return;
        try {
            const adaptedBody: NewSubstanceStudyBody = {
                idTipo: Number(formData.type),
                idTipoFrecuencia: Number(formData.frequency),
                detalle: formData.detail,
                cantidad: Number(formData?.frequencyQuantity)
            };

            const response: any = await createNewSubstanceStudy({
                reportId: selectedCarcinogenicReportId,
                substanceId: selectedSubstanceId,
                body: adaptedBody
            });

            if (response?.error) {
                if (response?.error?.data?.statusCode === 400) {
                    handleErrors(response?.error);
                    return;
                }
                enqueueSnackbar(t('newStudyForm.enqueues.create.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(t('newStudyForm.enqueues.create.success'), {
                    variant: 'success'
                });
            }
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo crear el estudio de sustancia',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };


    const handleDeleteSubstanceIconClick = (studyId: number) => {
        showDeleteStudyAlert({confirmAction: () => handleDeleteSubstanceStudy(studyId)});
    };

    const handleDeleteSubstanceStudy = async (studyId: number) => {
        if (!selectedCarcinogenicReportId || !selectedSubstanceId || !studyId) return;
        try {
            const response: any = deleteSubstanceStudy({
                reportId: selectedCarcinogenicReportId,
                substanceId: selectedSubstanceId,
                studyId: studyId
            });
              if (response?.error) {
                  if (response?.error?.data?.statusCode === 400) {
                      handleErrors(response?.error);
                      return;
                  }
                  enqueueSnackbar(t('newStudyForm.enqueues.delete.error'), {
                      variant: 'error'
                  });
              } else {
                  enqueueSnackbar(t('newStudyForm.enqueues.delete.success'), {
                      variant: 'success'
                  });
              }
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo eliminar el estudio de sustancia',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    }

    return {
        formData,
        errors,
        newStudyFieldsConfig,
        isCreatingNewSubstanceStudy,
        isDeletingSubstanceStudy,
        handleChange,
        validateForm,
        handleSubmitStudy,
        handleDeleteSubstanceIconClick
    };
};

export default useNewStudyForm;
